import { useRef, useState } from 'react';
import { api, type SeoDfsStatus, type SeoGoalState, type SeoGroupTrend, type SeoKeywordItem, type SeoKeywordSummary, type SeoMover, type SeoWorkType } from './api';
import { UyariSimgesi } from './WebSitesiGrafik';
import { sayi, tamSayi, tarihSaat } from './metin/ortak';
import { SEO, SEO_IS, SEO_KELIME as K, SEO_HEDEF_DURUMU, SEO_DFS_DURUM } from './metin/seo';
import { ALAN as A, cizgiYolu, paylar, siraEkseni, tutarEkseni, tutarY, xKonum, yKonum } from './seo-grafik';

const kisaHafta = (h: string) => h.slice(5);
const grupAdi = (key: string) => key === '' ? K.kumesiz : key;
const GOAL_SINIF: Readonly<Record<SeoGoalState, string>> = { REACHED: 'ok', NEAR: 'warn', FAR: 'danger', NO_TARGET: 'none', NO_READING: 'none' };

/** DFS sirasi bossa: ilk 100 disi ile olculmedi ayri yazilir; durum bilinmiyorsa tire. */
export function DfsHucre({ sira, durum }: { sira: number | null; durum: SeoDfsStatus | null }) {
  if (sira !== null) return <>{tamSayi(sira)}</>;
  return <>{durum === null ? SEO.eksik : (SEO_DFS_DURUM[durum] ?? SEO.eksik)}</>;
}

export function HedefEtiketi({ durum }: { durum: SeoGoalState }) {
  return <span className={`tag ${GOAL_SINIF[durum] === 'none' ? '' : GOAL_SINIF[durum]}`}>{SEO_HEDEF_DURUMU[durum]}</span>;
}

/** Yon: dikey ucgen + renk + metin. Renk tek basina anlam tasimaz; capraz ok kullanilmaz. */
export function SiraDegisimi({ fark }: { fark: number | null }) {
  if (fark === null) return <span>{SEO.eksik}</span>;
  if (fark === 0) return <span>{sayi(0)}</span>;
  const yukseldi = fark > 0;
  return <span className={`seo-delta ${yukseldi ? 'seo-up' : 'seo-down'}`}>
    <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false"><polygon points={yukseldi ? '5,1 9,9 1,9' : '1,1 9,1 5,9'} fill="currentColor" /></svg>
    <span>{yukseldi ? '+' : '-'}{sayi(Math.abs(fark))} {K.sira}</span>
    <span className="seo-sr">{yukseldi ? K.yukseldi : K.dustu}</span>
  </span>;
}

function Bilgi({ metin }: { metin: string }) {
  return <p className="muted seo-chart-empty" data-testid="seo-grafik-bos">{metin}</p>;
}

interface Seri { ad: string; deger: (number | null)[]; sinif: string }
function Gosterge({ seriler }: { seriler: Seri[] }) {
  return <p className="legend seo-legend">{seriler.map((s) => <span key={s.ad}><i className={`seo-line-swatch ${s.sinif}`} /> {s.ad} </span>)}</p>;
}

function XEtiketleri({ haftalar }: { haftalar: string[] }) {
  const adim = Math.max(1, Math.ceil(haftalar.length / 6));
  return <>{haftalar.map((h, i) => (i % adim === 0 || i === haftalar.length - 1) ? <text key={h} x={xKonum(i, haftalar.length)} y={A.yukseklik - 12} textAnchor="middle" className="seo-g-etiket">{kisaHafta(h)}</text> : null)}</>;
}

/** Sira grafigi: eksen ters (1 ustte). Hedef varsa kesik yatay cizgi. Deger yoksa cizgi kopar. */
export function CizgiGrafik({ ad, haftalar, seriler, hedef = null }: { ad: string; haftalar: string[]; seriler: Seri[]; hedef?: number | null }) {
  const eksen = siraEkseni(seriler.flatMap((s) => s.deger), hedef);
  if (eksen === null) return <Bilgi metin={K.azVeri} />;
  return <div className="seo-chart">
    <svg viewBox={`0 0 ${A.genislik} ${A.yukseklik}`} role="img" aria-label={ad} className="seo-grafik" data-testid="seo-grafik">
      <title>{ad}</title>
      {eksen.ticks.map((t) => <g key={t}><line x1={A.sol} x2={A.genislik - A.sag} y1={yKonum(t, eksen)} y2={yKonum(t, eksen)} className="seo-g-izgara" /><text x={A.sol - 8} y={yKonum(t, eksen) + 4} textAnchor="end" className="seo-g-etiket">{t}</text></g>)}
      <XEtiketleri haftalar={haftalar} />
      {hedef !== null && <g data-testid="seo-grafik-hedef"><line x1={A.sol} x2={A.genislik - A.sag} y1={yKonum(hedef, eksen)} y2={yKonum(hedef, eksen)} className="seo-g-hedef" /><text x={A.genislik - A.sag} y={yKonum(hedef, eksen) - 6} textAnchor="end" className="seo-g-hedef-yazi">{K.hedefCizgi} {hedef}</text></g>}
      {seriler.map((s) => {
        const { d, noktalar } = cizgiYolu(s.deger, eksen);
        return <g key={s.ad} className={s.sinif}>
          {d !== '' && <path d={d} className="seo-g-cizgi" fill="none" />}
          {noktalar.map((n) => <circle key={n.i} cx={n.x} cy={n.y} r="3.5" className="seo-g-nokta"><title>{s.ad}: {kisaHafta(haftalar[n.i]!)} {sayi(n.deger, 1)}</title></circle>)}
        </g>;
      })}
    </svg>
    <Gosterge seriler={seriler} />
  </div>;
}

/** Sutun grafigi: 0 tabanli normal eksen (gosterim). */
export function SutunGrafik({ ad, haftalar, deger }: { ad: string; haftalar: string[]; deger: (number | null)[] }) {
  const eksen = tutarEkseni(deger);
  if (eksen === null) return <Bilgi metin={K.azVeri} />;
  const w = (A.genislik - A.sol - A.sag) / haftalar.length;
  const kalin = Math.min(48, w * 0.6);
  return <div className="seo-chart">
    <svg viewBox={`0 0 ${A.genislik} ${A.yukseklik}`} role="img" aria-label={ad} className="seo-grafik" data-testid="seo-grafik-gosterim">
      <title>{ad}</title>
      {eksen.ticks.map((t) => <g key={t}><line x1={A.sol} x2={A.genislik - A.sag} y1={tutarY(t, eksen.max)} y2={tutarY(t, eksen.max)} className="seo-g-izgara" /><text x={A.sol - 8} y={tutarY(t, eksen.max) + 4} textAnchor="end" className="seo-g-etiket">{tamSayi(t)}</text></g>)}
      {haftalar.map((h, i) => {
        const v = deger[i];
        const x = A.sol + w * i + w / 2;
        const y0 = tutarY(0, eksen.max);
        return <g key={h}>
          {v !== null && v !== undefined && <rect x={x - kalin / 2} y={tutarY(v, eksen.max)} width={kalin} height={Math.max(1, y0 - tutarY(v, eksen.max))} className="seo-g-sutun"><title>{kisaHafta(h)} {tamSayi(v)}</title></rect>}
          <text x={x} y={A.yukseklik - 12} textAnchor="middle" className="seo-g-etiket">{kisaHafta(h)}</text>
        </g>;
      })}
    </svg>
  </div>;
}

const GOAL_SIRA: { anahtar: 'reached' | 'near' | 'far' | 'noTarget' | 'noReading'; ad: string; sinif: string }[] = [
  { anahtar: 'reached', ad: K.ulasti, sinif: 'seo-d-ok' }, { anahtar: 'near', ad: K.yaklasiyor, sinif: 'seo-d-warn' }, { anahtar: 'far', ad: K.uzak, sinif: 'seo-d-danger' },
  { anahtar: 'noReading', ad: K.okumaYok, sinif: 'seo-d-none' },
];

function HedefDagilimi({ ozet }: { ozet: SeoKeywordSummary }) {
  const g = ozet.goals;
  const hedefli = g.reached + g.near + g.far + g.noReading;
  if (hedefli === 0) return <div className="seo-card"><h3>{K.hedefDagilim}</h3><p className="muted" data-testid="seo-hedef-yok">{K.hedefYok}</p><p className="muted">{K.hedefsiz}: {tamSayi(g.noTarget)}</p></div>;
  const yuzde = paylar(GOAL_SIRA.map((s) => g[s.anahtar]));
  let x = 0;
  return <div className="seo-card" data-testid="seo-hedef-dagilim">
    <h3>{K.hedefDagilim}</h3>
    <svg viewBox="0 0 100 10" preserveAspectRatio="none" className="seo-dagilim-bar" role="img" aria-label={K.hedefDagilimAd}>
      <title>{K.hedefDagilimAd}</title>
      {GOAL_SIRA.map((s, i) => { const w = yuzde[i]!; const r = <rect key={s.anahtar} x={x} y="0" width={w} height="10" className={s.sinif} />; x += w; return r; })}
    </svg>
    <ul className="seo-dagilim-liste">
      {GOAL_SIRA.map((s, i) => <li key={s.anahtar} data-testid={`seo-hedef-${s.anahtar}`}><i className={`seo-swatch ${s.sinif}`} /> {s.ad}: <strong>{tamSayi(g[s.anahtar])}</strong> <span className="muted">(%{sayi(yuzde[i]!, 0)})</span></li>)}
      <li className="muted">{K.hedefsiz}: {tamSayi(g.noTarget)}</li>
    </ul>
    <p className="muted">{K.hedefAciklama}</p>
  </div>;
}

function HareketListesi({ ad, satirlar, bosMetin }: { ad: string; satirlar: SeoMover[]; bosMetin: string }) {
  return <div><h4>{ad}</h4>{satirlar.length === 0 ? <p className="muted">{bosMetin}</p> : <ul className="seo-hareket" aria-label={K.hareketAd}>{satirlar.map((m) => <li key={m.id} data-testid="seo-hareket-satir"><span data-veri>{m.term}</span> <SiraDegisimi fark={m.change} /> <span className="muted">{m.dfsRank !== null ? `(${tamSayi(m.dfsRank)})` : ''}</span></li>)}</ul>}</div>;
}

const GRUP_SINIF = ['seo-c0', 'seo-c1', 'seo-c2', 'seo-c3', 'seo-c4', 'seo-c5'];
function GrupGrafigi({ baslik, haftalar, gruplar }: { baslik: string; haftalar: string[]; gruplar: SeoGroupTrend[] }) {
  const seriler = gruplar.map((g, i) => ({ ad: `${grupAdi(g.key)} (${tamSayi(g.keywords)} ${K.kelimeAdedi})`, deger: g.points.map((p) => p.avgRank), sinif: GRUP_SINIF[i % GRUP_SINIF.length]! }));
  return <div className="seo-card"><h3>{baslik}</h3>
    {haftalar.length < 2 ? <Bilgi metin={K.azOkuma} /> : <><CizgiGrafik ad={baslik} haftalar={haftalar} seriler={seriler} /><p className="muted">{K.siraEkseniNot}</p></>}
  </div>;
}

export function KelimeOzeti({ ozet }: { ozet: SeoKeywordSummary }) {
  const yeniler = ozet.fresh;
  return <section aria-label={K.ozetBaslik} className="seo-ozet" data-testid="seo-kelime-ozet">
    <div className="stat-grid">
      <div className="stat"><span className="stat-label">{K.sonIceAktarim}</span><p className="stat-value" data-testid="seo-son-iceaktarim">{tarihSaat(ozet.lastImportAt)}</p></div>
      <div className="stat"><span className="stat-label">{K.kosuSayisi}</span><p className="stat-value" data-testid="seo-kosu-sayisi">{tamSayi(ozet.runCount)}</p></div>
      <div className="stat"><span className="stat-label">{K.sonHafta}</span><p className="stat-value">{ozet.lastWeekIso ?? SEO.olculmedi}</p></div>
    </div>
    <div className="seo-ozet-izgara">
      <HedefDagilimi ozet={ozet} />
      <div className="seo-card" data-testid="seo-hareketler">
        {ozet.movers.hasPrevious ? <>
          <HareketListesi ad={K.yukselen} satirlar={ozet.movers.up} bosMetin={K.hareketYok} />
          <HareketListesi ad={K.dusen} satirlar={ozet.movers.down} bosMetin={K.hareketYok} />
          <p className="muted">{K.degismedi}: {tamSayi(ozet.movers.unchanged)}</p>
        </> : <Bilgi metin={K.oncekiYok} />}
      </div>
      <GrupGrafigi baslik={K.kumeTrend} haftalar={ozet.weeks} gruplar={ozet.clusters} />
      <GrupGrafigi baslik={K.pazarTrend} haftalar={ozet.weeks} gruplar={ozet.markets} />
      <div className="seo-card"><h3>{K.gosterimTrend}</h3>
        {ozet.weeks.length < 2 ? <Bilgi metin={K.azOkuma} /> : <SutunGrafik ad={K.gosterimAd} haftalar={ozet.weeks} deger={ozet.impressions.map((p) => p.total)} />}
      </div>
      <div className="seo-card" data-testid="seo-yeni-kelimeler"><h3>{K.yeniBaslik}</h3>
        {yeniler.length === 0 ? <p className="muted">{K.yeniYok}</p> : <ul className="seo-hareket">{yeniler.map((y) => <li key={y.id} data-testid="seo-yeni-satir"><span data-veri>{y.term}</span> <span className="tag ok">{y.newness === 'PREVIOUS' ? K.yeniOnceki : y.newness === 'ADDED' ? K.yeniEklendi : K.yeniEtiket}</span> <span className="muted">{y.firstSeenWeek ?? ''}</span></li>)}</ul>}
      </div>
    </div>
  </section>;
}

/** Tek kelimenin ayrintisi: trend, hedef cizgisi, gosterim, okuma tablosu ve hedef formu. */
export function KelimeCekmecesi({ k, yazabilir, onDegisti }: { k: SeoKeywordItem; yazabilir: boolean; onDegisti: () => void }) {
  const haftalar = k.series.map((s) => s.weekIso);
  const [mesgul, setMesgul] = useState(false);
  const [sonuc, setSonuc] = useState<'hata' | 'tamam' | null>(null);
  const kilit = useRef(false);
  const kaydet = (govde: Parameters<typeof api.seoPatchKeyword>[1]) => {
    if (kilit.current) return;
    kilit.current = true; setMesgul(true); setSonuc(null);
    void api.seoPatchKeyword(k.id, govde).then(() => { setSonuc('tamam'); onDegisti(); }).catch(() => setSonuc('hata')).finally(() => { kilit.current = false; setMesgul(false); });
  };
  return <div className="seo-cekmece" data-testid="seo-kelime-cekmece">
    <h3 data-veri>{k.term}</h3>
    <p className="muted">{k.targetRank !== null ? <>{K.hedefDurumAciklama}: <strong>{tamSayi(k.targetRank)}</strong> · {K.suAn}: <strong>{(k.dfsRank ?? k.gscAvgPosition) === null ? SEO.olculmedi : sayi(k.dfsRank ?? k.gscAvgPosition, 1)}</strong> · </> : null}<HedefEtiketi durum={k.goal} />{k.goalGap !== null && k.goalGap > 0 ? <> · {K.fark}: {sayi(k.goalGap, 1)} {K.sirasi}</> : null}</p>
    {!k.trendReady ? <Bilgi metin={K.azOkuma} /> : <>
      <CizgiGrafik ad={`${K.trendAd}: ${k.term}`} haftalar={haftalar} hedef={k.targetRank} seriler={[
        { ad: K.dfsSeri, deger: k.series.map((s) => s.dfsRank), sinif: 'seo-c0' },
        { ad: K.gscSeri, deger: k.series.map((s) => s.gscAvgPosition), sinif: 'seo-c1' },
      ]} />
      <p className="muted">{K.siraEkseniNot}</p>
      <h4>{K.gosterimSeri}</h4>
      <SutunGrafik ad={`${K.gosterimSeri}: ${k.term}`} haftalar={haftalar} deger={k.series.map((s) => s.gscImpressions)} />
    </>}
    <div className="table-wrap" role="region" aria-label={K.okumaTablo} tabIndex={0}>
      <table><thead><tr>{[K.hafta, K.dfsSeri, K.gscSeri, K.gosterimSeri, K.hacimSeri].map((b) => <th scope="col" key={b}>{b}</th>)}</tr></thead>
        <tbody>{k.series.map((s) => <tr key={s.weekIso}><th scope="row">{s.weekIso}</th><td><DfsHucre sira={s.dfsRank} durum={s.dfsStatus} /></td><td>{s.gscAvgPosition === null ? SEO.eksik : sayi(s.gscAvgPosition, 1)}</td><td>{s.gscImpressions === null ? SEO.eksik : tamSayi(s.gscImpressions)}</td><td>{s.monthlyVolume === null ? SEO.eksik : tamSayi(s.monthlyVolume)}</td></tr>)}</tbody></table>
    </div>
    {yazabilir && <form className="seo-form" aria-label={K.tekBaslik} onSubmit={(e) => {
      e.preventDefault();
      const f = new FormData(e.currentTarget);
      const sira = String(f.get('targetRank') ?? '').trim();
      const sayfa = String(f.get('targetPage') ?? '').trim();
      const is = String(f.get('workType') ?? '');
      kaydet({ targetRank: sira === '' ? null : Number(sira), targetPage: sayfa === '' ? null : sayfa, workType: is === '' ? null : is as SeoWorkType });
    }}>
      <fieldset disabled={mesgul}><legend>{K.tekBaslik}</legend><div className="seo-fields">
        <label>{SEO.hedefSira}<input name="targetRank" type="number" min={1} max={100} step={1} defaultValue={k.targetRank ?? ''} /></label>
        <label>{SEO.hedefSayfa}<input name="targetPage" maxLength={240} defaultValue={k.targetPage ?? ''} placeholder={K.sayfaIpucu} /></label>
        <label>{SEO.is}<select name="workType" defaultValue={k.workType ?? ''}><option value="">{K.isTipiSec}</option>{(Object.keys(SEO_IS) as SeoWorkType[]).map((w) => <option key={w} value={w}>{SEO_IS[w]}</option>)}</select></label>
        <button type="submit" className="birincil">{SEO.kaydet}</button>
      </div></fieldset>
      {sonuc === 'hata' && <div className="callout callout-warn seo-note" role="alert"><UyariSimgesi />{SEO.yazmaHatasi}</div>}
      {sonuc === 'tamam' && <p role="status">{K.hedefKaydedildi}</p>}
    </form>}
  </div>;
}

/** Secili kelimelere toplu hedef. Bos alan degismez; "hedef sirayi temizle" ayri secenek. */
export function TopluHedef({ secili, onTemizle, onDegisti }: { secili: string[]; onTemizle: () => void; onDegisti: () => void }) {
  const [mesgul, setMesgul] = useState(false);
  const [sonuc, setSonuc] = useState<'hata' | 'tamam' | null>(null);
  const kilit = useRef(false);
  return <form className="seo-form seo-toplu" aria-label={K.topluBaslik} data-testid="seo-toplu" onSubmit={(e) => {
    e.preventDefault();
    if (kilit.current) return;
    const f = new FormData(e.currentTarget);
    const sira = String(f.get('targetRank') ?? '').trim();
    const sayfa = String(f.get('targetPage') ?? '').trim();
    const is = String(f.get('workType') ?? '');
    const temizle = f.get('clear') === 'on';
    const govde: Parameters<typeof api.seoBulkKeywordTarget>[0] = { ids: secili };
    if (temizle) govde.targetRank = null; else if (sira !== '') govde.targetRank = Number(sira);
    if (sayfa !== '') govde.targetPage = sayfa;
    if (is !== '') govde.workType = is as SeoWorkType;
    if (Object.keys(govde).length === 1) return;
    kilit.current = true; setMesgul(true); setSonuc(null);
    void api.seoBulkKeywordTarget(govde).then(() => { setSonuc('tamam'); onDegisti(); }).catch(() => setSonuc('hata')).finally(() => { kilit.current = false; setMesgul(false); });
  }}>
    <fieldset disabled={mesgul}><legend>{K.topluBaslik}: {tamSayi(secili.length)} {K.secili}</legend>
      <p className="muted">{K.topluNot}</p>
      <div className="seo-fields">
        <label>{SEO.hedefSira}<input name="targetRank" type="number" min={1} max={100} step={1} /></label>
        <label>{SEO.hedefSayfa}<input name="targetPage" maxLength={240} placeholder={K.sayfaIpucu} /></label>
        <label>{SEO.is}<select name="workType" defaultValue=""><option value="">{K.degistirme}</option>{(Object.keys(SEO_IS) as SeoWorkType[]).map((w) => <option key={w} value={w}>{SEO_IS[w]}</option>)}</select></label>
        <label className="seo-check"><input name="clear" type="checkbox" />{K.hedefiTemizle}</label>
        <button type="submit" className="birincil">{K.uygula}</button>
        <button type="button" onClick={onTemizle}>{K.secimiTemizle}</button>
      </div>
    </fieldset>
    {sonuc === 'hata' && <div className="callout callout-warn seo-note" role="alert"><UyariSimgesi />{SEO.yazmaHatasi}</div>}
    {sonuc === 'tamam' && <p role="status">{K.hedefKaydedildi}</p>}
  </form>;
}

